import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { apiFetch } from "./api";

export type FidcOption = {
  id: string;
  name: string;
  cnpj: string;
  pl_atual: string | number | null;
  gestora_name?: string | null;
};

type FidcState = {
  fidcId: string | null;
  setFidcId: (id: string | null) => void;
  fidcs: FidcOption[];
  loading: boolean;
  selected: FidcOption | null;
};

const FidcContext = createContext<FidcState | null>(null);

const STORAGE_KEY = "vscapital.fidc_id";

function readUrl(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("fidc");
}

function writeUrl(id: string | null): void {
  if (typeof window === "undefined") return;
  const u = new URLSearchParams(window.location.search);
  if (id) u.set("fidc", id); else u.delete("fidc");
  const qs = u.toString();
  const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
  window.history.replaceState(null, "", newUrl);
}

export function FidcProvider({ children }: { children: ReactNode }): JSX.Element {
  const [fidcs, setFidcs] = useState<FidcOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [fidcId, setFidcIdInternal] = useState<string | null>(() => {
    const url = readUrl();
    if (url) return url;
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  const setFidcId = useCallback((id: string | null) => {
    setFidcIdInternal(id);
    writeUrl(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    apiFetch<{ fidcs: FidcOption[] }>("/fidcs")
      .then((r) => setFidcs(r.fidcs ?? []))
      .catch(() => setFidcs([]))
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo<FidcOption | null>(() => {
    if (!fidcId) return null;
    return fidcs.find((f) => f.id === fidcId) ?? null;
  }, [fidcId, fidcs]);

  return (
    <FidcContext.Provider value={{ fidcId, setFidcId, fidcs, loading, selected }}>
      {children}
    </FidcContext.Provider>
  );
}

export function useFidc(): FidcState {
  const ctx = useContext(FidcContext);
  if (!ctx) throw new Error("useFidc must be used within FidcProvider");
  return ctx;
}

/** Helper para apender ?fidc=... a query strings de endpoints. */
export function withFidc(qs: string, fidcId: string | null): string {
  if (!fidcId) return qs;
  return qs ? `${qs}&fidc=${encodeURIComponent(fidcId)}` : `fidc=${encodeURIComponent(fidcId)}`;
}
